import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import { ColumnDefinition, renderColumn, sortableColumn } from 'components/TableColumn/TableColumn';
import { useCallAPI } from 'hooks/useCallAPI';
import styles from './storedValueDetail.module.scss';
import { SelectOptions } from 'components/commonType';
import { DefaultStoredValueManager, StoredValueManager } from 'core/storedValue/StoredValueManager';
import i18n from 'i18n';
import { useParams } from 'react-router-dom';
import { StoredValueLog, StoredValueTotal } from 'core/storedValue/StoredValueLog';
import {
  amountFormatter,
  freezeAmountFormatter,
  priceFormatter,
  amountFormatterForExcel,
  freezeAmountFormatterForExcel,
  priceFormatterForExcel,
  ExcelCellData
} from './PriceFormatter';
import { defaultTo } from 'lodash';
import { Agency, DefaultAgencyManager } from 'core';

export enum StoredValueLogColumns {
  DATE = 'storedValueId',
  DESCRIPTION = 'description',
  COMMENT = 'comment',
  AMOUNT = 'amount',
  REMAIN = 'remain',
  FREEZE_AMOUNT = 'freezeAmount'
}

const defaultAgencyManager = new DefaultAgencyManager();
const defaultStoredValueManager = new DefaultStoredValueManager();

export enum StoredValueEditType {
  NONE = 'none',
  ADD = 'add',
  EDIT = 'edit'
}

type StoredValueInfo = SelectOptions & { className?: string };

export const useStoredValueDetailModel = (
  storedValueManager: StoredValueManager = defaultStoredValueManager,
  agencyManager = defaultAgencyManager
) => {

  const params = useParams<{ agencyId: string }>();
  const agencyId: number | undefined = params.agencyId ? +params.agencyId : undefined;

  if (!agencyId) {
    throw new Error('Agency ID is required');
  }

  const { loading, callAPIs } = useCallAPI();
  const [storedValueLog, setStoredValueLog] = useState<StoredValueLog[]>([]);
  const [storedValueInfo, setStoredValueInfo] = useState<StoredValueInfo[]>([]);
  const [usedValueInfo, setUsedValueInfo] = useState<SelectOptions[]>([]);
  const [remainingValue, setRemainingValue] = useState<number>(0);
  const [agencyInfo, setAgencyInfo] = useState<{
    name: string;
    vendorNumber: string;
    currency: string;
  }>({
    name: '',
    vendorNumber: '',
    currency: 'NTD'
  });

  const [editType, setEditType] = useState<StoredValueEditType>(StoredValueEditType.NONE);

  /**
   * 上方四格數字的來源不同支，但**口徑一致**（ECOPD-5479）：
   *
   * - **剩餘額度 / 凍結額度**：來自後端（`/remaining-amount`、`/frozen-amount`）。
   * - **已用額度 / 總儲值**：由前端從明細清單自算（`getStoredValueSummary`）。
   *
   * 兩邊都是**營運口徑** —— RECONCILE_ADJUST（弭平扣除）是純帳務註記，後端與前端都不把它算進
   * 任何金額（只有後端的對帳報表才計）。所以供應商看不到弭平那一列，不會讓四格數字產生落差。
   *
   * 已用 / 總儲值之所以還是前端自算：`/remaining-amount` 是 PChome 的**對外 open API**，
   * 不為了後台顯示去動對外契約。
   */
  const initInfo = useCallback((
    storedValueLog: StoredValueLog[],
    storedValueTotal: StoredValueTotal,
    frozenTotal: StoredValueTotal,
    agencyInfo: {
      name: string;
      currency: string;
    }
  ) => {
    const currency = defaultTo(storedValueTotal.currency, agencyInfo.currency);
    // 已用 / 總儲值：前端自算，營運口徑（跳過純帳務註記 RECONCILE_ADJUST）—— 見上方註解。
    const storedValueSummary = storedValueManager.getStoredValueSummary(storedValueLog);
    const summaryOfAgency = defaultTo(storedValueSummary.find(item => item.adAgencyId === agencyId), {
      stored: 0,
      used: 0
    });
    const remainingAmount = defaultTo(storedValueTotal.remainingAmount, 0);

    setStoredValueInfo([{
      label: i18n.t<string>('storedValueDetail.labels.remainValue'),
      value: formatPriceWithCurrency(currency, remainingAmount),
      className: styles.remainValue
    }, {
      label: i18n.t<string>('storedValueDetail.labels.freezedValue'),
      value: formatPriceWithCurrency(currency, defaultTo(frozenTotal.frozenAmount, 0)),
      className: styles.freezedValue
    }]);
    setUsedValueInfo([{
      label: i18n.t<string>('storedValueDetail.labels.usedValue'),
      value: formatPriceWithCurrency(currency, summaryOfAgency.used)
    }, {
      label: i18n.t<string>('storedValueDetail.labels.totalValue'),
      value: formatPriceWithCurrency(currency, summaryOfAgency.stored)
    }]);
    setRemainingValue(remainingAmount);

  }, [storedValueManager, agencyId]);

  const initData = useCallback(async () => {
    callAPIs([
      () => storedValueManager.getStoredValueLogsByAgencyId(agencyId),
      () => agencyManager.fetchAgency(agencyId),
      () => storedValueManager.getStoredValueTotal(agencyId),
      () => storedValueManager.getStoredValueFrozenTotal(agencyId)
    ], (
      storedValueLogs,
      agency: Agency,
      storedValueTotal: StoredValueTotal,
      frozenTotal: StoredValueTotal
    ) => {
      const { detail } = agency;
      const agencyInfo = {
        name: detail.companyName,
        currency: detail.currency,
        vendorNumber: detail.vendorNumber
      };
      setAgencyInfo(agencyInfo);
      setStoredValueLog(storedValueLogs);
      initInfo(storedValueLogs, storedValueTotal, frozenTotal, agencyInfo);
    });
  }, [storedValueManager, agencyManager, agencyId, callAPIs, initInfo]);

  useEffect(() => {
    // clear location state
    window.history.replaceState({}, '');
    initData();
  }, [initData]);

  const columnDefinition = (columnName): ColumnDefinition => ({
    ...sortableColumn(columnName, `storedValueLog.headers.${columnName}`, true),
    classes: () => styles[columnName],
    headerClasses: () => styles[columnName]
  });

  const columns = useMemo(() => [
    renderColumn(columnDefinition(StoredValueLogColumns.DATE), (_, rowData: StoredValueLog) => rowData.createTime),
    renderColumn(columnDefinition(StoredValueLogColumns.DESCRIPTION)),
    renderColumn(columnDefinition(StoredValueLogColumns.COMMENT)),
    renderColumn(columnDefinition(StoredValueLogColumns.REMAIN), priceFormatter),
    renderColumn(columnDefinition(StoredValueLogColumns.AMOUNT), amountFormatter),
    renderColumn(columnDefinition(StoredValueLogColumns.FREEZE_AMOUNT), freezeAmountFormatter)
  ], []);

  const onEditValueModalClose = (refresh: boolean) => {
    setEditType(StoredValueEditType.NONE);
    if (refresh) {
      initData();
    }
  };

  const excelFormatters: Record<string, (value: any, record: StoredValueLog) => ExcelCellData> = useMemo(() => ({
    [StoredValueLogColumns.REMAIN]: priceFormatterForExcel,
    [StoredValueLogColumns.AMOUNT]: amountFormatterForExcel,
    [StoredValueLogColumns.FREEZE_AMOUNT]: freezeAmountFormatterForExcel
  }), []);

  const getExcelData = useCallback(() => {
    const metricsMapI18n = columns.reduce((acc, setting) => {
      acc[setting.dataField] = i18n.t<string>(setting.text);
      return acc;
    }, {} as {[key: string]: string});

    const redCells: { row: number; col: number }[] = [];

    // Sort by createTime (the displayed value of storedValueId column) desc
    const sortedStoredValueLog = [...storedValueLog].sort((a, b) =>
      new Date(b.createTime).getTime() > new Date(a.createTime).getTime() ? 1 : -1
    );

    const excelDataList = sortedStoredValueLog.reduce((excelData, rowData, rowIndex) => {
      const newRowData = {};
      columns.forEach((column, colIndex) => {
        const key = column.dataField;
        const excelFormatter = excelFormatters[key];

        if (excelFormatter) {
          const result = excelFormatter(rowData[key], rowData);
          newRowData[metricsMapI18n[key]] = result.text;
          if (result.isRed) {
            redCells.push({ row: rowIndex + 1, col: colIndex }); // +1 for header row
          }
        } else {
          newRowData[metricsMapI18n[key]] = column.formatter ? column.formatter(rowData[key], rowData, rowIndex) : rowData[key];
        }
      });
      excelData.push(newRowData);
      return excelData;
    }, [] as {[key: string]: string}[]);

    return { excelDataList, redCells };
  }, [columns, storedValueLog, excelFormatters]);

  const exportRecords = useCallback(async () => {
    const { excelDataList, redCells } = getExcelData();
    const XLSXStyle = await import(/* webpackChunkName: "xlsx-js-style" */ 'xlsx-js-style');
    const XLSX = XLSXStyle.default;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelDataList);

    // Apply red font color to negative value cells
    redCells.forEach(({ row, col }) => {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      if (ws[cellAddress]) {
        ws[cellAddress].s = {
          font: { color: { rgb: 'FF0000' } }
        };
      }
    });

    // Set fixed column width
    const colCount = Object.keys(excelDataList[0] || {}).length;
    ws['!cols'] = Array(colCount).fill({ wpx: 212 });

    XLSX.utils.book_append_sheet(wb, ws, 'report');
    XLSX.writeFile(wb, 'storeValueLog.xlsx');
  }, [getExcelData]);

  return {
    agencyId,
    loading,
    columns,
    editType,
    storedValueLog,
    storedValueInfo,
    usedValueInfo,
    remainingValue,
    agencyInfo,
    onEditValueModalClose,
    setEditType,
    exportRecords
  };
};
